import {
  CONTRACT_STATUS, CONSOLIDATED, NAV_LOCK_LEVEL,
  LOCKED_ROUTES_PARTIAL, LEARN_MORE_TYPE
} from 'src/utils/constants/contract-termination'
import i18n from 'src/assets/i18n/i18n'
import { adobeCjaEventEmitter } from 'modules/adobe-cja/utils'
import { CJA_EVENTS, LINK_TYPES, LINK_CATEGORIES, LINK_SECTION, LINK_NAMES } from 'modules/adobe-cja/constants'

export function isNonActiveStatus(status) {
  return status === CONTRACT_STATUS.TERMINATING || status === CONTRACT_STATUS.TERMINATED
}

export function isTerminatingOrTerminated(foundry) {
  return isNonActiveStatus(foundry?.contractStatus)
}

export function isEarningsReportFoundryVisible(foundry) {
  if (!isTerminatingOrTerminated(foundry)) return true
  return Boolean(foundry?.isAdmin)
}

export function openTerminationLearnMoreLink(type, t) {
  const linkKey = type === LEARN_MORE_TYPE.TERMINATION
    ? 'pages.contract.termination.termination_process_link'
    : 'pages.contract.termination.rejoin_link'
  adobeCjaEventEmitter({
    event: CJA_EVENTS.LINK_CLICK,
    links: {
      linkCategory: LINK_CATEGORIES.AGREEMENTS,
      linkSection: LINK_SECTION.AGREEMENTS,
      linkName: type === LEARN_MORE_TYPE.TERMINATION ? LINK_NAMES.LEARN_MORE_CONTRACT_TERMINATION : LINK_NAMES.LEARN_MORE_REJOINING_FOUNDRY,
      linkType: LINK_TYPES.BUTTON
    }
  })
  const link = t(linkKey)
  window.open(link, '_blank', 'noopener,noreferrer')
}

export function handleTerminationSignAgreementClick({ foundry, linkSection, linkCategory, router, includeFont = false }) {
  if (foundry?.pimContractSummary === 'Monotype IP') {
    return
  }

  const payload = {
    event: CJA_EVENTS.LINK_CLICK,
    links: {
      linkCategory: linkCategory || LINK_CATEGORIES.AGREEMENTS,
      linkSection,
      linkName: LINK_NAMES.SIGN_LATEST_AGREEMENT,
      linkType: LINK_TYPES.BUTTON
    }
  }
  if (includeFont && foundry?.name) {
    payload.font = [{ fontFoundary: foundry.name }]
  }
  adobeCjaEventEmitter(payload)
  router.push(`/sign-agreement/${foundry.foundryId}`)
}

export function getNavLockLevel({ foundriesTerminated, isUserAdmin }) {
  if (foundriesTerminated !== CONSOLIDATED.ALL) return NAV_LOCK_LEVEL.NONE
  if (isUserAdmin === CONSOLIDATED.NONE) return NAV_LOCK_LEVEL.FULL
  return NAV_LOCK_LEVEL.PARTIAL
}

export function daysUntilTermination(terminationDateStr) {
  if (!terminationDateStr) return null
  const termDate = new Date(terminationDateStr + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffMs = termDate.getTime() - today.getTime()
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  return Math.max(0, days)
}

export function formatTerminationDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

export function partitionFoundriesByContractStatus(foundries) {
  const terminating = []
  const terminated = []
  const active = []

  for (const f of foundries) {
    if (f.contractStatus === CONTRACT_STATUS.TERMINATING) {
      terminating.push(f)
    } else if (f.contractStatus === CONTRACT_STATUS.TERMINATED) {
      terminated.push(f)
    } else {
      active.push(f)
    }
  }

  return { terminating, terminated, active }
}

export function shouldShowTerminationModal(foundriesTerminated) {
  return foundriesTerminated !== CONSOLIDATED.NONE
}

export function buildTerminationModalProps(foundries, isUserAdmin) {
  const { terminating, terminated } = partitionFoundriesByContractStatus(foundries)
  const sections = []

  const sortByDateThenName = (a, b) => {
    const dateCmp = (a.terminationDate || '').localeCompare(b.terminationDate || '')
    return dateCmp !== 0 ? dateCmp : (a.name || '').localeCompare(b.name || '')
  }

  if (terminating.length > 0) {
    const sorted = [...terminating].sort(sortByDateThenName)
    const mapped = sorted.map(f => ({
      name: f.name,
      terminationDate: formatTerminationDate(f.terminationDate),
      daysLeft: daysUntilTermination(f.terminationDate)
    }))
    const daysValues = mapped.map(f => f.daysLeft).filter(d => d !== null)

    sections.push({
      status: 'terminating',
      foundries: mapped,
      earliestDate: formatTerminationDate(sorted[0].terminationDate),
      daysLeft: daysValues.length > 0 ? Math.min(...daysValues) : null
    })
  }

  if (terminated.length > 0) {
    const sorted = [...terminated].sort(sortByDateThenName)
    sections.push({
      status: 'terminated',
      foundries: sorted.map(f => ({
        name: f.name,
        terminationDate: formatTerminationDate(f.terminationDate)
      }))
    })
  }

  const isMixed = sections.length === 2
  const hasTerminating = sections.some(s => s.status === 'terminating')

  let titleKey = 'foundry_contract_popup.title_terminated_single'
  let titleParams = {}

  if (isMixed) {
    titleKey = 'foundry_contract_popup.title_mixed'
  } else if (hasTerminating) {
    const terminatingSection = sections.find(s => s.status === 'terminating')
    const foundryName = terminatingSection.foundries[0]?.name || ''
    if (terminatingSection.foundries.length === 1) {
      titleKey = 'foundry_contract_popup.title_terminating_single'
      titleParams = { name: foundryName }
    } else {
      titleKey = 'foundry_contract_popup.title_terminating_multiple'
      titleParams = { name: foundryName, count: terminatingSection.foundries.length - 1 }
    }
  } else {
    const terminatedSection = sections.find(s => s.status === 'terminated')
    const foundryName = terminatedSection?.foundries[0]?.name || ''
    if (terminatedSection && terminatedSection.foundries.length === 1) {
      titleKey = 'foundry_contract_popup.title_terminated_single'
      titleParams = { name: foundryName }
    } else if (terminatedSection) {
      titleKey = 'foundry_contract_popup.title_terminated_multiple'
      titleParams = { name: foundryName, count: terminatedSection.foundries.length - 1 }
    }
  }

  const { t } = i18n().global

  return {
    isAdmin: isUserAdmin !== CONSOLIDATED.NONE,
    sections,
    titleKey,
    titleParams,
    learnMoreLinks: {
      terminationProcess: t('foundry_contract_popup.learn_more_termination_url'),
      reJoining: t('foundry_contract_popup.learn_more_rejoin_url')
    },
    showFooter: true
  }
}

export function getPartialLockRouteNames() {
  return [...LOCKED_ROUTES_PARTIAL]
}

export function evaluateFoundrySwitch(foundryId, isUserAdmin) {
  const foundries = JSON.parse(window.localStorage.getItem('userFoundries') || '[]')
  const target = foundries.find(f => f.id === foundryId)
  if (!target) return { block: false, modalProps: null }
  const blocked = isNonActiveStatus(target.contractStatus)
  const props = blocked ? buildTerminationModalProps(foundries, isUserAdmin) : null
  return { block: blocked, modalProps: props }
}

export function getSessionFoundrySelectValues() {
  const prevId = window.localStorage.getItem('sessionFoundryId')
  const prevName = window.localStorage.getItem('sessionFoundryName')
  return { obj: { value: prevId, label: prevName }, value: 'value', name: 'label' }
}

export function attemptFoundrySwitch({ foundryId, isUserAdmin, store, revertSelection }) {
  const { block, modalProps } = evaluateFoundrySwitch(foundryId, isUserAdmin)
  if (!block) return false
  store.commit('auth/setContractTerminationModal', { show: true, props: modalProps })
  revertSelection?.()
  return true
}

export function findBlockedFoundryInList(items, getFoundryId, isUserAdmin) {
  for (const item of items) {
    const result = evaluateFoundrySwitch(getFoundryId(item), isUserAdmin)
    if (result.block) return { item, modalProps: result.modalProps }
  }
  return null
}

export function getFoundrySubTitle(contractStatus) {
  if (!contractStatus) return ''
  const lower = contractStatus.toLowerCase()
  const { t } = i18n().global
  if (lower === 'terminated') return t('foundry_status.terminated')
  if (lower === 'terminating') return t('foundry_status.terminating')
  return ''
}
